<template>
  <GraphqlDataTable
    :query="query"
    :order-by="initialOrder"
    :column-black-list="
      isAgencyLink ? ['id', 'OwnerStaffingAgencyColumn', 'OwnerResponsibilityColumn'] : ['id']
    "
    :filter-black-list="isAgencyLink ? ['accounts', 'staffingAgencies'] : ['accounts']"
    :additional-query-variables="{ client: clientRelationId }"
    :search-placeholder="isAgencyLink ? $t('Search MSP team…') : $t('Search recruiters…')"
    max-height-adjustment="15vh"
  >
    <template #no-results>
      <div v-if="isAgencyLink && recruiterTotal === 0" data-test="client-msp-team-empty">
        <NoTrustedContactsFound no-actions>
          <template #main>{{ $t('No one from the MSP is assigned to this client yet') }}</template>
          <template #sub>{{ $t('The MSP assigns its own people to each client.') }}</template>
        </NoTrustedContactsFound>
      </div>
      <div
        v-else-if="linkedAgencies === 0 || recruiterTotal === 0"
        :data-test="
          linkedAgencies === 0
            ? 'client-agency-recruiters-no-agencies'
            : 'client-agency-recruiters-empty'
        "
      >
        <NoTrustedContactsFound>
          <template #main>
            <template v-if="linkedAgencies === 0">{{ $t('No staffing agencies linked yet') }}</template>
            <template v-else>{{ $t('No recruiters assigned yet') }}</template>
          </template>
          <template #sub>
            <template v-if="linkedAgencies === 0">
              <div>
                {{ $t('Link a staffing agency to :client.', { client: clientName ?? '' }) }}
              </div>
              <div>
                {{ $t('Once linked, the agency can assign its own recruiters, and they’ll appear here.') }}
              </div>
            </template>
            <template v-else>
              <div>
                {{
                  $tc(
                    ':client has :count linked staffing agency but no linked recruiters yet. | :client has :count linked staffing agencies but no linked recruiters yet.',
                    linkedAgencies ?? 0,
                    { client: clientName ?? '', count: linkedAgencies ?? 0 },
                  )
                }}
              </div>
              <div>{{ $t('Each agency assigns its own recruiters.') }}</div>
            </template>
          </template>
          <template #actions>
            <WdsButton
              primary
              data-test="client-agency-recruiters-view-agencies"
              @click="emit('openAgencies')"
            >
              {{ $t('View staffing agencies') }}
            </WdsButton>
          </template>
        </NoTrustedContactsFound>
      </div>
      <WdsText
        v-else-if="recruiterTotal !== null"
        secondary
        data-test="client-agency-recruiters-no-match"
      >
        {{ $t('No one matches your search or filters') }}
      </WdsText>
    </template>
  </GraphqlDataTable>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsButton, WdsText } from '@worksome/wds-components'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import NoTrustedContactsFound from '@/components/Contacts/CompanyFreelancer/NoTrustedContactsFound.vue'
import { graphql } from '@/gql'
import { SortOrder, ClientRelationAgencyOwnerOrderByColumn } from '@/gql/graphql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'

const query = graphql(`
  query ClientAgencyRecruitersTableQuery(
    $accounts: [ID!]
    $client: ID!
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [ClientRelationAgencyOwnerOrderByClauseInput!]
    $staffingAgencies: [ID!]
    $assignedAtDateRange: DateRangeInput
  ) {
    clientRelationCounterpartOwners(
      accounts: $accounts
      client: $client
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      staffingAgencies: $staffingAgencies
      assignedAtDateRange: $assignedAtDateRange
    ) {
      data {
        id
        ...OwnerNameColumn
        ...OwnerStaffingAgencyColumn
        ...OwnerResponsibilityColumn
        ...OwnerEmailColumn
        ...OwnerPhoneColumn
        ...OwnerAssignedAtColumn
      }
    }
  }
`) as TypedQueryDocumentNode

withDefaults(
  defineProps<{
  clientRelationId: string
  /** The MSP's people on a staffing agency's link, rather than agency recruiters on a client relation. */
  isAgencyLink?: boolean
  /** Unfiltered counts, null until known; they pick which empty state applies. */
  recruiterTotal: number | null
  linkedAgencies: number | null
  clientName: string | null
}>(),
  { isAgencyLink: false },
)

const emit = defineEmits<{ (e: 'openAgencies'): void }>()

const initialOrder = {
  field: ClientRelationAgencyOwnerOrderByColumn.Name,
  order: SortOrder.Asc,
}
</script>
